import useCloudFunctionCall from "@/hooks/useCloudFunctionCall";
import { useNavigate, useParams } from "react-router-dom";
import {
  AuthenticationProps,
  AuthenticationResponse,
} from "../../types/authenticationTypes";
import { brand } from "branchAndBrandSpecific/utils/brandVariables/brandVariables";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Flex,
  Heading,
  Spinner,
  Text,
  TextField,
} from "@radix-ui/themes";
import { useEffect, useState } from "react";
import { Loader } from "@/components";
import { siteRoutes } from "@/routes/siteRoutesList";
import i18next from "i18next";
import { auth } from "@/api/firebase/firebase.config";
import firebase from "firebase/compat/app";

export const AutoVerifyEmailRedirect = () => {
  const { email, code } = useParams();
  const navigate = useNavigate();
  const [values, setValues] = useState<{
    password: string;
    repeatPassword: string;
  }>({
    password: "",
    repeatPassword: "",
  });

  const isPasswordValid =
    /^(?=.*[0-9])(?=.*[!@#$%^&*()+\-\\[\]{};:'",.<>/?]).{6,}$/.test(
      values.password
    ); // Check if password contains every rule
  const passwordsMatch = values.password === values.repeatPassword;

  const setNewValue = (targetName: string, value: string) => {
    return setValues((prev) => ({
      ...prev,
      [targetName]: value,
    }));
  };

  const [response, _loading, _error, authenticate] =
    useCloudFunctionCall<AuthenticationResponse>(
      "userAuthentication",
      undefined
    );

  const baseAuthenticationProps = {
    method: "email",
    emailAddress: email,
    platform: brand.brandValue,
    tenantId: brand.tenantId,
  } as AuthenticationProps;

  const handleVerify = async () => {
    if (!passwordsMatch) {
      return;
    }

    await authenticate({
      ...baseAuthenticationProps,
      action: "convert-to-permanent",
      emailAddress: email,
      autoVerify: true,
      siteDomain: brand.site,
      verificationCode: code,
    }).catch(() => {
      return toast.error("Email is not available, please choose another.");
    });
  };

  const handleConversion = async () => {
    const credential = firebase.auth.EmailAuthProvider.credential(
      email,
      values.password
    );

    await auth.currentUser.linkWithCredential(credential);
    await auth.currentUser?.reload();
    navigate(siteRoutes["profile"]);
    toast.success("Email verified!");
  };

  useEffect(() => {
    if (response && response?.status === "user-verified") {
      handleConversion();
    }
  }, [response]);

  useEffect(() => {
    if (_error && (!response || response?.status !== "user-verified")) {
      toast.error(_error.message);
    }
  }, [_error]);

  if (_error?.message) {
    return (
      <>
        <Flex align="center" justify="center" className="loader">
          <Flex align="center" gap="2" direction="column">
            <Flex direction="column" gap="2" align="center">
              <Heading>Oops, verification failed.</Heading>
              <Text size="2">{_error?.message}</Text>
            </Flex>
          </Flex>
        </Flex>
      </>
    );
  }

  if (_loading) {
    return (
      <>
        <Loader>
          <Flex direction="column" gap="2" align="center">
            <Heading>Verifying</Heading>
            <Text size="2">Hang on, don't refresh ...</Text>
          </Flex>
        </Loader>
      </>
    );
  }

  if (!_loading && !_error) {
    return (
      <Flex align="center" justify="center" height="100vh">
        <Box>
          <Heading size="5">Enter a password for your account</Heading>
          <Flex align="center" direction="column" gap="4">
            <Flex direction="column" gap="4">
              <Box>
                {/* Password */}
                <Text as="label" size="2">
                  {i18next.t("auth.input.password.create.label")}
                </Text>
                <TextField.Root
                  // placeholder={i18next.t(
                  //   "auth.input.password.create.placeholder"
                  // )}
                  size="3"
                  type="password"
                  value={values.password}
                  onChange={(e) => {
                    setNewValue("password", e.target.value);
                  }}
                />
              </Box>
              {values.password && isPasswordValid && (
                <Box>
                  {/* Repeat password */}
                  <Text as="label" size="2">
                    {i18next.t("auth.input.password.repeat.label")}
                  </Text>
                  <TextField.Root
                    // placeholder={i18next.t(
                    //   "auth.input.password.repeat.placeholder"
                    // )}
                    size="3"
                    type="password"
                    value={values.repeatPassword}
                    onChange={(e) => {
                      setNewValue("repeatPassword", e.target.value);
                    }}
                    onKeyDown={async (e) => {
                      if (e.key === "Enter") {
                        if (
                          values.repeatPassword !== "" &&
                          values.repeatPassword === values.repeatPassword
                        ) {
                          handleVerify();
                        }
                      }
                    }}
                  />
                </Box>
              )}
            </Flex>

            <Box>
              <Button
                variant="soft"
                color={isPasswordValid && passwordsMatch ? "blue" : "gray"}
                size="3"
                disabled={!passwordsMatch || !isPasswordValid}
                onClick={() => handleVerify()}
              >
                Verify account
              </Button>
            </Box>
          </Flex>
        </Box>
      </Flex>
    );
  }
};
